import Swal from 'sweetalert2';
import {TranslateService} from '../services/translate.service';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {FileService} from '../services/file.service';

export const FILE_SIZE = 10485760;
export const VIDEO_SIZE = 104857600;

export const popUpFileTooLarge = (translateService: TranslateService) => {
  const words = translateService.data;

  Swal.fire({
    icon: 'error',
    title: `${words['FILE_TOO_LARGE'].toUpperCase()}`,
    html: `${words['FILE_YOU_ADDED_EXCEEDS_THE_MAXIMUM_LIMIT']}`,
    showConfirmButton: false,
    showCancelButton: false,
  });
};

export const popUpFileExtensionInvalid = (translateService: TranslateService) => {
  const words = translateService.data;

  Swal.fire({
    icon: 'error',
    title: `${words['FILE_INVALID'].toUpperCase()}`,
    html: `${words['FILE_YOU_ADDED_IS_INVALID']}`,
    showConfirmButton: false,
    showCancelButton: false,
  });
};

export const popUpFileFromGoogleDrive = (translateService: TranslateService) => {
  const words = translateService.data;

  Swal.fire({
    icon: 'error',
    title: `${words['FILE_FROM_GOOGLE_DRIVE'].toUpperCase()}`,
    html: `${words['CANT_RETRIEVE_FILE_FROM_GOOGLE_DRIVE']}`,
    showConfirmButton: false,
    showCancelButton: false,
  });
};

export const validationFileExtension = async (file: File, typesAllowed: Array<string>, translateService: TranslateService) => {
  return new Promise(resolve => {
    let isFileWrong = null;
    const filetype = file.type;
    let types = [];
    let match = true;
    const fileReader = new FileReader();
    fileReader.onloadend = (e) => {
      // @ts-ignore
      const arr = (new Uint8Array(e.target.result)).subarray(0, 4);
      let header = '';
      for (let i = 0; i < arr.length; i++) {
        header += arr[i].toString(16);
      }

      if (header === '') {
        popUpFileFromGoogleDrive(translateService);
        resolve(true);
        return;
      }


      switch (header) {
        case '89504e47':
          types = ['image/png'];
          break;
        case '47494638':
          types = ['image/gif'];
          break;
        case 'ffd8ffe0':
        case 'ffd8ffe1':
        case 'ffd8ffe2':
          types = ['image/jpeg'];
          break;
        case '3c3f786d':
          types = ['image/svg+xml'];
          break;
        case '00018':
        case '00020':
          types = ['video/mp4'];
          break;
        case '52494646':
          types = ['video/avi', 'audio/wav'];
          break;
        case '00014':
          types = ['video/quicktime'];
          break;
        case '1a45dfa3':
          types = ['video/x-matroska'];
          break;
        case '001ba':
          types = ['video/mpeg'];
          break;
        case 'fffb824':
        case '4944334':
        case '4944333':
          types = ['audio/mpeg'];
          break;
        case '0001c':
          types = ['audio/x-m4a'];
          break;
        case '52494646':
          types = ['audio/wav'];
          break;
        case 'fff15080':
          types = ['audio/vnd.dlna.adts'];
          break;
        case '25504446':
          types = ['application/pdf'];
          break;
        case '':
        case '504b34':
        case 'd0cf11e0':
          types = [
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            'application/vnd.ms-powerpoint',
            'application/x-zip-compressed',
            'application/zip',
            'application/x-rar-compressed',
            'application/x-7z-compressed',
            'application/gzip',
            'application/vnd.android.package-archive',
            'application/x-compressed',
            'application/rar'
          ];
          break;
        case '52617221':
          types = [
            'application/x-compressed',
            'application/x-rar-compressed',
            'application/rar'
          ];
          break;
        case '44617461':
        case '6f6e4164':
        case '54756761':
        case '68747470':
          types = ['text/plain'];
          break;
        case '504b34':
          types = ['application/x-zip-compressed'];
          break;
      }


      if (file.type !== 'text/plain') {
        match = false;
        for (const type of types) {
          if (file.type === type) {
            match = true;
            break;
          }
        }
      } else {
        match = true;
      }

      if (!match) {
        isFileWrong = true;
      } else {
        isFileWrong = true;
        for (const typeAllowed of typesAllowed) {
          if (filetype === typeAllowed) {
            isFileWrong = false;
            break;
          }
        }
      }
      if (file.name.endsWith('.rar') && header === '52617221') {
       isFileWrong = false;
      }
      if (isFileWrong) {
        popUpFileExtensionInvalid(translateService);
      }

      resolve(isFileWrong);
    };

    fileReader.readAsArrayBuffer(file);
  });
};

export const createFileThumbnail = async (file: File, domSanitizer: DomSanitizer) => {
  return new Promise<SafeResourceUrl>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event: any) => {
      resolve(domSanitizer.bypassSecurityTrustResourceUrl(event.target.result));
    };
    reader.readAsDataURL(file);
  });
};

export const validateFiles = async (files: File[], allowedFileTypes: Array<string>, translateService: TranslateService, fileSize = FILE_SIZE, videoSize = VIDEO_SIZE) => {
  for (const file of files) {
    // Validate File Size
    const size = file.size;
    if (file.type.includes('video')) {
      if (size > videoSize) {
        popUpFileTooLarge(translateService);
        return false;
      }
    } else {
      if (size > fileSize) {
        popUpFileTooLarge(translateService);
        return false;
      }
    }

    // Validate File Type
    const isFileTypeWrong = await validationFileExtension(file, allowedFileTypes, translateService);
    if (isFileTypeWrong) {
      return false;
    }
  }

  return true;
};

export const uploadFiles = async (files: File[], path: string, fileService: FileService) => {
  Swal.fire({
    title: 'Uploading Files',
    showConfirmButton: false,
    showCancelButton: false,
    allowOutsideClick: false,
  });
  Swal.showLoading();

  const uploadedFiles = [];
  try {
    for (const file of files) {
      const res = await uploadFile(file, path, fileService);
        // Swal.close();
        // Swal.fire({
        //   title: 'Uploading Files',
        //   html: `
        //     <style>
        //     .swal2-popup .swal-progress {
        //       width: 100%;
        //       max-width: 420px;
        //       height: 12px;
        //       background: #e9ecef;
        //       border-radius: 999px;
        //       overflow: hidden;
        //       margin: 12px auto 0;
        //       box-shadow: inset 0 1px 2px rgba(0,0,0,0.06);
        //     }
        //     .swal2-popup .swal-bar {
        //       width: 0%;
        //       height: 100%;
        //       background: linear-gradient(90deg, #2f80ed, #56ccf2);
        //       border-radius: 999px;
        //       color: #fff;
        //       display: flex;
        //       align-items: center;
        //       justify-content: center;
        //       font: 500 12px/1 system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
        //       letter-spacing: .2px;
        //       transition: width 200ms ease;
        //     }
        //     </style>
        //     <div class="swal-progress">
        //       <div class="swal-bar" id="swalProgressBar">0%</div>
        //     </div>
        //     <div id="swalProgressText" style="margin-top:8px;font:500 12px/1 system-ui;color:#495057;">0%</div>
        //   `,
        //   allowOutsideClick: false,
        //   showConfirmButton: false,
        //   showCancelButton: false,
        //   focusConfirm: false
        // });
      const uploadedFile = {
        name: res['name'],
        type: res['type'],
        path: res['path'],
        url: res['url'],
        status: res['status'],
        bunny: res['bunny'],
      };
      uploadedFiles.push(uploadedFile);
    }
  } catch (e) {
    console.log(e);
  } finally {
    Swal.close();
  }

  return uploadedFiles;
};

export const uploadFile = async (file: File, path: string, fileService: FileService, hideNotification = null) => {
  return new Promise(async (resolve, reject) => {
    const value = {
      name: file.name,
      type: file.type,
      path: path,
    };
    await fileService.getFilesSignedUrls(value)
      .subscribe(async response => {
        // file['url'] = response.data.url.substring(0, response.data.url.signedUrl.search(/\?/));
        file['url'] = response.data.url.substring(0, response.data.url.search(/\?/));
        file['path'] = response.data.path;
        file['status'] = await fileService.uploadS3Sync(response.data.url, file);
        file['bunny'] = response.data.bunny;
        resolve(file);
      }, error => {
        file['status'] = false;
        resolve(file);
      });
  });
};

function base64ToFile(base64: string, filename: string, mimeType: string): File {
  const byteCharacters = atob(base64.split(',')[1]); // Remove `data:image/png;base64,`
  const byteArrays = [];
  for (let i = 0; i < byteCharacters.length; i++) {
    byteArrays.push(byteCharacters.charCodeAt(i));
  }
  const blob = new Blob([new Uint8Array(byteArrays)], { type: mimeType });
  return new File([blob], filename, { type: mimeType });
}

async function urlToFile(imageUrl: string, filename: string, mimeType: string): Promise<File> {
  try {
    const response = await fetch(imageUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.statusText}`);
    }
    const blob = await response.blob();
    return new File([blob], filename, { type: mimeType });
  } catch (error) {
    console.error('Error fetching image:', error);
    throw error; // Re-throw the error after logging it
  }
}

export const imageFileTypes = [
  'image/png',
  'image/gif',
  'image/jpeg',
];

export const imageFileExtensions = [
  'png',
  'gif',
  'jpeg',
];

export const videoFileTypes = [
  'video/mp4',
  'video/avi',
  'video/quicktime',
  'video/x-matroska',
  'video/mpeg',
  'video/mov',
];

export const videoFileExtensions = [
  'mp4',
  'avi',
  'mov',
];

export const audioFileTypes = [
  'audio/mpeg',
  'audio/x-m4a',
  'audio/wav',
  'audio/vnd.dlna.adts',
];

export const audioFileExtensions = [
  'mp3',
  'm4a',
  'wav',
  'mpeg',
];

export const pdfFileTypes = [
  'application/pdf',
];

export const pdfFileExtensions = [
  'pdf',
];

export const officeFileTypes = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-powerpoint',
];

export const officeFileExtensions = [
  'doc',
  'docx',
  'xls',
  'xlsx',
  'ppt',
  'pptx',
];

export const textFileTypes = [
  'text/plain',
];

export const textFileExtensions = [
  'txt',
];

export const compressedFileTypes = [
  'application/x-zip-compressed',
  'application/zip',
  'application/x-rar-compressed',
  'application/x-7z-compressed',
  'application/gzip',
  'application/vnd.android.package-archive',
  'application/x-compressed',
  'application/rar',
];

export const compressedFileExtensions = [
  'zip',
  'rar'
];
